import React, { useState, useRef, useCallback, useEffect, useMemo } from "react"
import { FormHandles } from "@unform/core"

import {
  FiPlus,
  FiRefreshCcw,
  FiRotateCcw,
  FiSearch,
  FiUser,
  FiUsers,
} from "react-icons/fi"

import IClasses from "../../models/IClasses"
import IStudents from "../../models/IStudents"

import Header from "../../components/Header"
import Sidebar from "../../components/Sidebar"
import Input from "../../components/InputUnform"
import Autocomplete from "../../components/AutocompleteUnform"
import RegistersButton from "../../components/RegistersButton"
import Pagination from "../../components/Pagination"
import DatePickerInput from "../../components/DatePickerInput"

import * as Yup from "yup"
import getValidationErrors from "../../utils/getValidationErrors"

import {
  Container,
  Content,
  Main,
  GroupBox,
  TabButton,
  TabsContainer,
  FieldsContainer,
  StyledForm,
  ButtonsContainer,
  SearchContainer,
  SearchCard,
  SmallButton,
  GradesContainer,
  TestsContainer,
  TestsCard,
  TestsWrapper,
  AbsencesContainer,
  Table,
  TableHeader,
  TableData,
} from "./styles"

import { useToast } from "../../hooks/toast"
import api from "../../services/api"

import IClassTests from "../../models/IClassTests"
import IStudentGrade from "../../models/IStudentGrades"
import IAbsences from "../../models/IAbsences"
import returnBackendErrors from "../../utils/returnBackendError"

interface Options {
  id: string
  description: string
}

interface RegisterStudentFormData {
  name: string
  class_id: string
}

const Students: React.FC = () => {
  const formRef = useRef<FormHandles>(null)
  const serachContainerRef = useRef<HTMLDivElement>(null)

  const { addToast } = useToast()

  const [classes, setClasses] = useState<Options[]>([])
  const [activeTab, setActiveTab] = useState("Cadastro")

  const [studentName, setStudentName] = useState<string>("")
  const [selectedStudent, setSelectedStudent] = useState<IStudents | undefined>(
    undefined
  )
  const [classTests, setClassTests] = useState<IClassTests[]>([])
  const [studentGrades, setStudentGrades] = useState<IStudentGrade[]>([])
  const [average, setAverage] = useState<number>(0)

  const [initialDateGrades, setInitialDateGrades] = useState<Date | undefined>(
    undefined
  )
  const [finalDateGrades, setFinalDateGrades] = useState<Date | undefined>(
    undefined
  )
  const [grades, setGrades] = useState<{ [key: string]: string }>({})

  const [initialDateAbsences, setInitialDateAbsences] = useState<
    Date | undefined
  >(new Date())
  const [finalDateAbsences, setFinalDateAbsences] = useState<Date | undefined>(
    new Date()
  )
  const [absences, setAbsences] = useState<IAbsences[]>([])

  //pesquisa
  const [students, setStudents] = useState<IStudents[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [limit, setLimit] = useState(0)
  const [nameSearch, setNameSearch] = useState("")

  const gradesMap = useMemo(() => {
    const map: Record<string, number> = {}
    studentGrades.forEach((grade) => {
      map[grade.test_id] = grade.grade
    })
    return map
  }, [studentGrades])

  const fetchData = useCallback(async () => {
    const response = await api.get("/classes/all")

    const schoolClasses: IClasses[] = response.data.classes

    const formattedClasses = schoolClasses.map((schoolClass) => {
      return { id: schoolClass.id, description: schoolClass.name }
    })

    setClasses(formattedClasses)
  }, [])

  const handleClearAbsences = useCallback(() => {
    setInitialDateAbsences(undefined)
    setFinalDateAbsences(undefined)
    setAbsences([])
  }, [])

  const handleClear = useCallback(() => {
    formRef.current?.clearField("name")
    formRef.current?.clearField("class_id")

    setInitialDateGrades(undefined)
    setFinalDateGrades(undefined)

    setClassTests([])
    setStudentGrades([])
    setGrades({})
    setStudentName("")
    setSelectedStudent(undefined)

    handleClearAbsences()
  }, [handleClearAbsences])

  const handleClearGrades = useCallback(
    async (student: IStudents | undefined) => {
      try {
        const gradesResponse = await api.get("/grades/", {
          params: {
            student_id: student?.id,
            class_id: student?.class_id,
            initialDate: "",
            finalDate: "",
          },
        })

        const testsResponse = await api.get("/class-tests/", {
          params: {
            class_id: student?.class_id,
            initialDate: "",
            finalDate: "",
          },
        })

        const averageResponse = await api.get("/grades/average", {
          params: {
            student_id: student?.id,
            class_id: student?.class_id,
            initialDate: "",
            finalDate: "",
          },
        })

        setClassTests(testsResponse.data.tests)
        setStudentGrades(gradesResponse.data.grades)
        setAverage(averageResponse.data.average)

        setInitialDateGrades(undefined)
        setFinalDateGrades(undefined)
      } catch (error) {
        addToast({
          type: "error",
          title: "Erro ao Limpar Pesquisa",
          description: "Ocorreu um erro ao limpar pesquisa!",
        })
      }
    },
    [addToast]
  )

  const handleSearchGrades = useCallback(
    async (student: IStudents | undefined) => {
      try {
        setGrades({})

        let initialFormatted = ""
        if (initialDateGrades) {
          initialFormatted = `${initialDateGrades.getFullYear()}-${(
            initialDateGrades.getMonth() + 1
          )
            .toString()
            .padStart(2, "0")}-${initialDateGrades
            .getDate()
            .toString()
            .padStart(2, "0")}`
        }

        let finalFormatted = ""
        if (finalDateGrades) {
          finalFormatted = `${finalDateGrades.getFullYear()}-${(
            finalDateGrades.getMonth() + 1
          )
            .toString()
            .padStart(2, "0")}-${finalDateGrades
            .getDate()
            .toString()
            .padStart(2, "0")}`
        }

        const gradesResponse = await api.get("/grades/", {
          params: {
            student_id: student?.id,
            class_id: student?.class_id,
            initialDate: initialFormatted,
            finalDate: finalFormatted,
          },
        })

        const testsResponse = await api.get("/class-tests/", {
          params: {
            class_id: student?.class_id,
            initialDate: initialFormatted,
            finalDate: finalFormatted,
          },
        })

        const averageResponse = await api.get("/grades/average", {
          params: {
            student_id: student?.id,
            class_id: student?.class_id,
            initialDate: initialFormatted,
            finalDate: finalFormatted,
          },
        })

        setClassTests(testsResponse.data.tests)
        setStudentGrades(gradesResponse.data.grades)
        setAverage(averageResponse.data.average)
      } catch (error) {
        addToast({
          type: "error",
          title: "Erro ao Pesquisar",
          description:
            "Ocorreu um erro ao pesquisar, tente novamente mais tarde",
        })
      }
    },
    [addToast, initialDateGrades, finalDateGrades]
  )

  const handleSearchAbsences = useCallback(
    async (student: IStudents | undefined) => {
      let initialFormatted = ""
      if (initialDateAbsences) {
        initialFormatted = `${initialDateAbsences.getFullYear()}-${(
          initialDateAbsences.getMonth() + 1
        )
          .toString()
          .padStart(2, "0")}-${initialDateAbsences
          .getDate()
          .toString()
          .padStart(2, "0")}`
      }

      let finalFormatted = ""
      if (finalDateAbsences) {
        finalFormatted = `${finalDateAbsences.getFullYear()}-${(
          finalDateAbsences.getMonth() + 1
        )
          .toString()
          .padStart(2, "0")}-${finalDateAbsences
          .getDate()
          .toString()
          .padStart(2, "0")}`
      }

      try {
        const absencesResponse = await api.get("/absences/list", {
          params: {
            student_id: student?.id,
            class_id: student?.class_id,
            initialDate: initialFormatted,
            finalDate: finalFormatted,
          },
        })

        setAbsences(absencesResponse.data.absences)
      } catch (error) {
        addToast({
          title: "Erro",
          type: "error",
          description: returnBackendErrors(error),
        })
      }
    },
    [initialDateAbsences, finalDateAbsences, addToast]
  )

  const handleSearch = useCallback(async () => {
    try {
      const response = await api.get("/students", {
        params: {
          page,
          name: nameSearch,
          limit,
        },
      })

      setStudents(response.data.students)
      setTotalPages(response.data.totalPages)
    } catch (err) {
      addToast({
        type: "error",
        title: "Erro ao Pesquisar",
        description: "Ocorreu um erro ao pesquisar, tente novamente mais tarde",
      })
    }
  }, [addToast, page, limit, nameSearch])

  const handleGradeChange = (testId: string, value: string) => {
    setGrades((prev) => ({
      ...prev,
      [testId]: value,
    }))
  }

  const handlePageClick = useCallback((page: number) => {
    setPage(page)
  }, [])

  const handleSelectStudent = useCallback(
    async (student: IStudents | undefined) => {
      handleSearchAbsences(student)
      handleSearchGrades(student)
      setStudentName(student ? student.name : "")
      setSelectedStudent(student)
    },
    [handleSearchGrades, handleSearchAbsences]
  )

  const handleDelete = useCallback(async () => {
    if (!selectedStudent) {
      addToast({
        type: "error",
        title: "Erro ao transferir",
        description: "Nenhum aluno selecionado para transferir, verifique",
      })
    }

    try {
      await api.patch(`/student/${selectedStudent?.id}/transferred`, {
        transferred: true,
      })

      handleClear()
      handleSearch()
    } catch (error) {
      addToast({
        title: "Erro",
        type: "error",
        description: returnBackendErrors(error),
      })
    }
  }, [selectedStudent, addToast, handleClear, handleSearch])

  const handleGrade = useCallback(
    async (test: IClassTests, reset: boolean) => {
      try {
        if (reset) {
          const gradeToDelete = studentGrades.find(
            (grade) => grade.test_id === test.id
          )

          if (gradeToDelete) {
            await api.delete(`/grade/${gradeToDelete.id}`)
          } else {
            addToast({
              type: "error",
              title: "Erro ao remover nota",
              description: "A nota a ser removida não foi encontrada.",
            })
            return // Para a execução se a nota não for encontrada
          }
        } else {
          try {
            await api.post("/grade", {
              student_id: selectedStudent?.id,
              test_id: test.id,
              grade: Number(grades[test.id]),
            })
          } catch (error) {
            addToast({
              title: "Erro",
              type: "error",
              description: returnBackendErrors(error),
            })

            return
          }
        }

        handleSearchGrades(selectedStudent)

        addToast({
          type: "success",
          title: "Parabéns!",
          description: `Nota ${reset ? "removida" : "cadastrada"} com sucesso!`,
        })
      } catch (error) {
        addToast({
          type: "error",
          title: "Erro no Cadastro",
          description:
            "Ocorreu um erro ao incluir a nota, tente novamente mais tarde",
        })
      }
    },
    [addToast, selectedStudent, grades, handleSearchGrades, studentGrades]
  )

  const handleSubmit = useCallback(
    async (data: RegisterStudentFormData) => {
      try {
        formRef.current?.setErrors({})

        const schema = Yup.object().shape({
          name: Yup.string().required("Nome do aluno é obrigatório"),
          class_id: Yup.string().required("Turma do aluno é obrigatória"),
        })

        await schema.validate(data, {
          abortEarly: false,
        })

        if (selectedStudent) {
          try {
            await api.patch(`/student/${selectedStudent.id}`, {
              name: data.name,
              class_id: data.class_id,
            })
          } catch (error) {
            addToast({
              title: "Erro",
              type: "error",
              description: returnBackendErrors(error),
            })

            return
          }
        } else {
          try {
            await api.post("/student", {
              name: data.name,
              class_id: data.class_id,
            })
          } catch (error) {
            addToast({
              title: "Erro",
              type: "error",
              description: returnBackendErrors(error),
            })

            return
          }
        }
      } catch (err) {
        addToast({
          type: "error",
          title: "Erro no Cadastro",
          description:
            "Ocorreu um erro ao fazer cadastro, tente novamente mais tarde",
        })

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err)

          formRef.current?.setErrors(errors)

          return
        }
      }

      handleSearch()
      handleClear()

      addToast({
        type: "success",
        title: "Parabéns!",
        description: "Cadastro realizado com sucesso",
      })
    },
    [addToast, handleSearch, handleClear, selectedStudent]
  )

  useEffect(() => {
    if (limit > 0) {
      handleSearch()
    }
  }, [page, limit, nameSearch, handleSearch])

  useEffect(() => {
    const updateLimit = () => {
      const altura = serachContainerRef.current?.clientHeight
      if (altura) {
        setLimit(Math.floor(altura / 65))
      }
    }

    updateLimit()

    window.addEventListener("resize", updateLimit)

    return () => window.removeEventListener("resize", updateLimit)
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return (
    <Container>
      <Header />

      <Main>
        <Sidebar />

        <Content>
          <h1>Cadastro de Alunos</h1>

          <GroupBox>
            <TabsContainer>
              <TabButton
                isActive={activeTab === "Cadastro"}
                onClick={() => setActiveTab("Cadastro")}
              >
                Cadastro
              </TabButton>
              <TabButton
                isActive={activeTab === "Notas"}
                onClick={() => setActiveTab("Notas")}
              >
                Notas
              </TabButton>
              <TabButton
                isActive={activeTab === "Faltas"}
                onClick={() => setActiveTab("Faltas")}
              >
                Faltas
              </TabButton>
            </TabsContainer>

            {activeTab === "Cadastro" && (
              <StyledForm ref={formRef} onSubmit={handleSubmit}>
                <FieldsContainer>
                  <Input
                    name="name"
                    icon={FiUser}
                    type="text"
                    placeholder="Nome do Aluno"
                    containerStyle={{ flex: 3 }}
                    value={studentName}
                    onChange={(e) => setStudentName(e.target.value)}
                  />

                  <Autocomplete
                    name="class_id"
                    icon={FiUsers}
                    type="text"
                    placeholder="Turma"
                    defaultValueId={selectedStudent?.class_id}
                    suggestions={classes}
                    containerStyle={{ flex: 1 }}
                  />
                </FieldsContainer>

                <h3>Pesquisa</h3>

                <FieldsContainer>
                  <Input
                    name="nameSearch"
                    icon={FiUser}
                    type="text"
                    placeholder="Nome do Aluno"
                    value={nameSearch}
                    onChange={(e) => setNameSearch(e.target.value)}
                    containerStyle={{ flex: 3 }}
                  />
                </FieldsContainer>

                <SearchContainer ref={serachContainerRef}>
                  {students.map((student) => (
                    <SearchCard
                      key={student.id}
                      transferred={student.transferred}
                      onClick={() => {
                        handleSelectStudent(student)
                      }}
                    >
                      <h3>{`${student.name} ${
                        student.transferred ? " - Transferido(a)" : ""
                      }`}</h3>
                      <p>{student.school_class.name}</p>
                    </SearchCard>
                  ))}
                </SearchContainer>

                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageClick={handlePageClick}
                />

                <ButtonsContainer>
                  <RegistersButton type="submit">Salvar</RegistersButton>
                  <RegistersButton
                    type="button"
                    disabled={selectedStudent?.transferred}
                    onClick={handleDelete}
                  >
                    Transferido
                  </RegistersButton>
                  <RegistersButton type="reset" onClick={handleClear}>
                    Limpar
                  </RegistersButton>
                </ButtonsContainer>
              </StyledForm>
            )}

            {activeTab === "Notas" && (
              <GradesContainer>
                <FieldsContainer>
                  <DatePickerInput
                    selected={initialDateGrades}
                    onSelect={setInitialDateGrades}
                  />

                  <DatePickerInput
                    selected={finalDateGrades}
                    onSelect={setFinalDateGrades}
                  />

                  <SmallButton
                    className="search"
                    type="button"
                    onClick={() => handleSearchGrades(selectedStudent)}
                  >
                    <FiSearch />
                  </SmallButton>

                  <SmallButton
                    className="search"
                    type="button"
                    onClick={() => {
                      handleClearGrades(selectedStudent)
                    }}
                  >
                    <FiRefreshCcw />
                  </SmallButton>
                </FieldsContainer>

                <TestsWrapper>
                  <TestsContainer>
                    {classTests.map((test) => (
                      <TestsCard key={test.id}>
                        <span>
                          <p>{`Data: ${new Date(test.date)
                            .getDate()
                            .toString()
                            .padStart(2, "0")}/${(
                            new Date(test.date).getMonth() + 1
                          )
                            .toString()
                            .padStart(2, "0")}/${new Date(
                            test.date
                          ).getFullYear()}`}</p>
                          <h3>
                            {test.description}{" "}
                            {test.weight > 0 && `(Peso: ${test.weight})`}
                          </h3>
                        </span>

                        <span>
                          <label>Nota:</label>
                          <input
                            type="number"
                            disabled={gradesMap.hasOwnProperty(test.id)}
                            value={grades[test.id] ?? gradesMap[test.id] ?? ""}
                            onChange={(e) =>
                              handleGradeChange(test.id, e.target.value)
                            }
                          />
                          <button
                            type="button"
                            onClick={() =>
                              handleGrade(
                                test,
                                gradesMap.hasOwnProperty(test.id)
                              )
                            }
                            className={
                              gradesMap.hasOwnProperty(test.id)
                                ? "reset"
                                : "default"
                            }
                          >
                            {gradesMap.hasOwnProperty(test.id) ? (
                              <FiRotateCcw />
                            ) : (
                              <FiPlus />
                            )}
                          </button>
                        </span>
                      </TestsCard>
                    ))}
                  </TestsContainer>
                </TestsWrapper>

                <h2>{`Média: ${average ? average : 0}`}</h2>
              </GradesContainer>
            )}

            {activeTab === "Faltas" && (
              <AbsencesContainer>
                <FieldsContainer>
                  <DatePickerInput
                    selected={initialDateAbsences}
                    onSelect={setInitialDateAbsences}
                  />

                  <DatePickerInput
                    selected={finalDateAbsences}
                    onSelect={setFinalDateAbsences}
                  />

                  <SmallButton
                    className="search"
                    type="button"
                    onClick={() => handleSearchAbsences(selectedStudent)}
                  >
                    <FiSearch />
                  </SmallButton>

                  <SmallButton
                    className="search"
                    type="button"
                    onClick={handleClearAbsences}
                  >
                    <FiRefreshCcw />
                  </SmallButton>
                </FieldsContainer>

                <Table>
                  <thead>
                    <tr>
                      <TableHeader>Data</TableHeader>
                      <TableHeader>Observação</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {absences.map((absence) => (
                      <tr key={absence.id}>
                        <TableData>
                          {new Date(absence.date).toLocaleDateString("pt-BR")}
                        </TableData>
                        <TableData>{absence.obs}</TableData>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </AbsencesContainer>
            )}
          </GroupBox>
        </Content>
      </Main>
    </Container>
  )
}

export default Students
